import { useMutation, useQuery } from '@tanstack/react-query';

import { getJobServiceSelection, getRescheduleReasons, getServiceSelection, putServiceSelection } from 'queries';
import { parseErrors, Toast, useJob } from 'helpers';
import { JsonDataItem, ReschedulingSteps } from 'types';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useExtraQuestionsData } from 'pages/booking/location/hooks';

export const useRescheduleDetails = (jobId?: string) => {
  const [selectedReason, setSelectedReason] = useState<number | null>(null);
  const [serviceSelection, setServiceSelection] = useState<JsonDataItem | null>(null);
  const { getExtraQuestionsData } = useExtraQuestionsData();
  const navigate = useNavigate();

  const { data: serviceList, isInitialLoading: isServiceListLoading } = useQuery(['service-selection'], getServiceSelection, {
    enabled: !!jobId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { job: jobData, isLoading: isJobDataLoading } = useJob(jobId);

  const { isInitialLoading: isSelectedServicesLoading } = useQuery(
    ['check-selected-services', jobId],
    getJobServiceSelection,
    {
      enabled: !!jobId,
      onSuccess: (data: JsonDataItem) => setServiceSelection(data),
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  const { data: rescheduleReasons, isInitialLoading: isReasonsLoading } = useQuery(
    ['get-reschedule-reasons'],
    getRescheduleReasons,
    {
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  const { mutate, isLoading: updatingServiceSelection } = useMutation(putServiceSelection, {
    onSuccess: (data: JsonDataItem) => {
      setServiceSelection(data.service_selection);
      data.extra_job_description_added &&
        Toast.show({
          description: 'The scope of the job has been automatically added to the job description',
          type: Toast.Type.Success,
        });
      navigate(`/job/reschedule/${jobId}/${ReschedulingSteps.SelectSlot}`);
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const updateServiceSelection = (data: JsonDataItem) => {
    // setProductSelection(data);
    mutate({
      products: data.products,
      selectionId: serviceSelection?.ID,
      customerId: jobData?.customer_ID,
      devices: data.device.map((name: string) => {
        return {
          id: serviceList?.parent.options.find((device: JsonDataItem) => device.name === name)?.id,
          qty: 1,
        };
      }),
      os: data.os?.map((os: string) => {
        return {
          [os]: 1,
        };
      }),
      description: data.description,
      serviceType: data.serviceType,
      previousJob: data.jobId,
      warrantyReason: data.warrantyReason,
      productAdditionalSelection: getExtraQuestionsData(data),
    });
  };

  const isDataLoading =
    isReasonsLoading ||
    isSelectedServicesLoading ||
    isJobDataLoading ||
    isServiceListLoading ||
    updatingServiceSelection;

  return {
    selectedReason,
    setSelectedReason,
    updateServiceSelection,
    serviceList,
    jobData,
    serviceSelection,
    rescheduleReasons,
    isDataLoading,
  };
};
